<script setup lang="ts">
import { ActionButton, StatusTag } from '@taler/ui'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  name: string
  notes: string | undefined
  isSubmitting: boolean
  errorMessage: string | undefined
}>()
const emit = defineEmits<{ save: [input: { name: string; notes: string }]; cancel: [] }>()

const { t } = useI18n()
const name = ref(props.name)
const notes = ref(props.notes ?? '')

const canSubmit = computed(() => name.value.trim().length > 0 && name.value.trim().length <= 200)

function submit(): void {
  if (!canSubmit.value) return
  emit('save', { name: name.value.trim(), notes: notes.value.trim() })
}
</script>

<template>
  <form class="flex flex-col items-start gap-3" @submit.prevent="submit">
    <label class="flex w-full max-w-md flex-col gap-1">
      <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
        t('contacts.nameLabel')
      }}</span>
      <input
        v-model="name"
        type="text"
        maxlength="200"
        required
        autofocus
        class="h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[color:var(--color-text)]"
      />
    </label>

    <label class="flex w-full max-w-md flex-col gap-1">
      <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
        t('contacts.notesLabel')
      }}</span>
      <textarea
        v-model="notes"
        maxlength="1000"
        rows="3"
        class="rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] py-[var(--space-inline)] text-[color:var(--color-text)]"
      />
    </label>

    <StatusTag v-if="errorMessage" tone="danger">{{ errorMessage }}</StatusTag>

    <div class="flex gap-2">
      <ActionButton type="submit" :disabled="!canSubmit || isSubmitting">
        {{ isSubmitting ? t('contacts.saving') : t('contacts.save') }}
      </ActionButton>
      <button
        type="button"
        class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-3 py-2 text-[color:var(--color-text)]"
        @click="emit('cancel')"
      >
        {{ t('contacts.cancel') }}
      </button>
    </div>
  </form>
</template>
